import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Form, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from "sweetalert2";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { ref, set } from "firebase/database";
import login from "../images/hero-1.png";

export default function RegisterPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });

  const [referralBy, setReferralBy] = useState(null);

  const [acceptTerms, setAcceptTerms] = useState(false); // <-- NEW

  // ------------------------
  // DETECT REFERRAL FROM URL
  // ------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferralBy(ref);
      console.log("Referred By:", ref);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --------------------------
  // FORM SUBMIT
  // --------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, email, phone, password, confirmPassword } = formData;

    if (!acceptTerms) {
      Swal.fire({
        icon: "warning",
        title: "Accept Terms & Conditions",
        text: "Please accept the terms and conditions before registering."
      });
      return;
    }

    if (!username || !email || !phone || !password || !confirmPassword) {
      Swal.fire({
        icon: "warning",
        title: "All fields are required",
        text: "Please fill in every field before submitting."
      });
      return;
    }

    if (password !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Passwords do not match",
        text: "Please ensure both password fields match."
      });
      return;
    }

    try {
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
        background: "transparent",
        backdrop: `rgba(0, 0, 0, 0.4)`
      });

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send verification email
      await sendEmailVerification(user);

      // Save user data to Realtime Database
      await set(ref(db, `blackstone-users/${user.uid}`), {
        username,
        email,
        phone,
        password,
        uid: user.uid,
        status: "NonVerified",
        referralBy: referralBy || null,
        walletBalance: 0
      });

      Swal.close();
      Swal.fire("Registration Successful!", "Verification email sent.", "success");

      navigate("/login");

    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: error.message
      });
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center vh-100"
      style={{ backgroundColor: "#87CEEB" }}
    >
      <div
        className="d-flex w-75 shadow-lg"
        style={{ backgroundColor: "#fff", borderRadius: "15px", overflow: "hidden" }}
      >
        {/* Left section: image */}
        <div className="w-50">
          <img
            src={login}
            alt="Register"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>

        {/* Right section */}
        <div className="w-50 p-5 d-flex flex-column justify-content-center">
          <Container>
            <h2 className="mb-2 fw-bold">Register</h2>

            <div className="divider-container mb-4">
              <div className="divider-line"></div>
              <span className="divider-text">Enter your details</span>
              <div className="divider-line"></div>
            </div>

            <Form onSubmit={handleSubmit}>
              
              {/* Referral Info */}
              {referralBy && (
                <div className="mb-3 p-2 bg-light border rounded">
                  <strong>Referred By:</strong> {referralBy}
                </div>
              )}

              <Form.Group className="mb-3" controlId="formUsername">
                <Form.Label className="fw-bold loginform-lebel">Username</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <div className="row mb-4">
                <Form.Group className="col-md-6 mb-3 mb-md-0" controlId="formEmail">
                  <Form.Label className="fw-bold loginform-lebel">Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="col-md-6" controlId="formPhone">
                  <Form.Label className="fw-bold loginform-lebel">Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </div>

              <div className="row mb-4">
                <Form.Group className="col-md-6 mb-3 mb-md-0" controlId="formPassword">
                  <Form.Label className="fw-bold loginform-lebel">Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="col-md-6" controlId="formConfirmPassword">
                  <Form.Label className="fw-bold loginform-lebel">Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </div>

              {/* Terms & Conditions Checkbox */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={
                    <span>
                      I accept the{" "}
                      <a
                        href="https://theblackstone.org.in/images/TermsandConditions.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "underline", fontWeight: "bold" }}
                      >
                        Terms & Conditions
                      </a>
                    </span>
                  }
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                />
              </Form.Group>

              <Button
                type="submit"
                className="w-100 gradient-btn border-0 py-2 fw-bold mb-3"
                disabled={!acceptTerms}   // <-- BUTTON DISABLED UNTIL CHECKED
              >
                Register
              </Button>
            </Form>
          </Container>

          <p className="text-center mt-3">
            Already have an account? <a href="/login">Sign In</a>
          </p>
        </div>
      </div>
    </div>
  );
}
